import { Users, Search, Phone, Mail, Loader2, Edit, Archive, Filter, Trash2, RotateCcw, Eye, EyeOff } from "lucide-react"
import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Layout } from "@/components/Layout"
import { 
  useStaffMembers, 
  useDeleteStaffMember, 
  useArchivedStaffMembers,
  useHardDeleteStaffMember,
  useRestoreStaffMember,
  StaffType, 
  StaffMember 
} from "@/hooks/useStaffMembers"
import NovoColaboradorModal from "@/components/NovoColaboradorModal"
import { resolvePhotoUrl } from "@/lib/db/db"
import EditarColaboradorModal from "@/components/EditarColaboradorModal"
import { useAuth } from "@/hooks/useAuth"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

const Colaboradores = () => {
  const { data: colaboradores, isLoading, error } = useStaffMembers()
  const { data: colaboradoresArquivados, isLoading: isLoadingArchived } = useArchivedStaffMembers()
  const { profile, loading: authLoading } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [filterType, setFilterType] = useState<StaffType | "all">("all")
  const [editModal, setEditModal] = useState<{ open: boolean; colaborador?: StaffMember }>({ open: false })
  const [activeTab, setActiveTab] = useState("active")
  const deleteStaffMember = useDeleteStaffMember()
  const hardDeleteStaffMember = useHardDeleteStaffMember()
  const restoreStaffMember = useRestoreStaffMember()

  useEffect(() => {
    console.log('Página Colaboradores - estado atual:', {
      authLoading,
      profile: profile ? {
        id: profile.id,
        role: profile.role,
        condominium_id: profile.condominium_id,
        full_name: profile.full_name
      } : null,
      colaboradores: colaboradores?.length,
      colaboradoresArquivados: colaboradoresArquivados?.length,
      isLoading,
      error: error?.message
    })
  }, [colaboradores, colaboradoresArquivados, isLoading, error, profile, authLoading])

  // Verificar se pode gerenciar colaboradores
  const canManageStaff = profile?.role === 'sindico' || profile?.role === 'administracao' || profile?.role === 'superadmin'

  const handleArchiveStaff = async (staffId: string, staffName: string) => {
    try {
      console.log('Arquivando colaborador:', staffId)
      await deleteStaffMember.mutateAsync(staffId)
    } catch (error: any) {
      console.error('Erro ao arquivar colaborador:', error)
    }
  }

  const handleHardDeleteStaff = async (staffId: string, staffName: string) => {
    try {
      console.log('Excluindo permanentemente colaborador:', staffId)
      await hardDeleteStaffMember.mutateAsync(staffId)
    } catch (error: any) {
      console.error('Erro ao excluir colaborador permanentemente:', error)
    }
  }

  const handleRestoreStaff = async (staffId: string, staffName: string) => {
    try {
      console.log('Restaurando colaborador:', staffId)
      await restoreStaffMember.mutateAsync(staffId)
    } catch (error: any) {
      console.error('Erro ao restaurar colaborador:', error)
    }
  }

  const getStaffTypeLabel = (type: StaffType) => {
    const labels = {
      porteiro: "Porteiro",
      zelador: "Zelador",
      jardineiro_piscineiro: "Jardineiro/Piscineiro",
      auxiliar_servicos_gerais: "Auxiliar de Serviços Gerais",
      gerente: "Gerente",
      operador: "Operador"
    }
    return labels[type]
  }

  const getStaffTypeBadgeColor = (type: StaffType) => {
    const colors = {
      porteiro: "bg-blue-100 text-blue-800",
      zelador: "bg-green-100 text-green-800",
      jardineiro_piscineiro: "bg-yellow-100 text-yellow-800",
      auxiliar_servicos_gerais: "bg-purple-100 text-purple-800",
      gerente: "bg-red-100 text-red-800",
      operador: "bg-orange-100 text-orange-800"
    }
    return colors[type]
  }

  const filterStaffList = (staffList: StaffMember[] | undefined) => {
    return staffList?.filter(colaborador => {
      const searchLower = searchTerm.toLowerCase()
      const fullName = colaborador.full_name || ""
      const email = colaborador.email || ""
      const phone = colaborador.phone || ""
      const cpf = colaborador.cpf || ""
      
      const matchesSearch = fullName.toLowerCase().includes(searchLower) ||
                           email.toLowerCase().includes(searchLower) ||
                           phone.includes(searchTerm) ||
                           cpf.includes(searchTerm.replace(/\D/g, ''))
      
      const matchesFilter = filterType === "all" || colaborador.staff_type === filterType
      
      return matchesSearch && matchesFilter
    }) || []
  }

  const filteredColaboradores = filterStaffList(colaboradores)
  const filteredArquivados = filterStaffList(colaboradoresArquivados)

  if (authLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-gray-600">Carregando usuário...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!profile) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro: Usuário não autenticado. Faça login novamente.
          </div>
        </div>
      </Layout>
    )
  }

  if (!canManageStaff) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Acesso negado: Apenas síndicos e administradores podem gerenciar colaboradores.
          </div>
        </div>
      </Layout>
    )
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-gray-600">Carregando colaboradores...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar colaboradores: {error.message}
          </div>
        </div>
      </Layout>
    )
  }

  const getInitials = (name: string | null) => {
    if (!name) return "?"
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  console.log('Renderizando página com colaboradores filtrados:', filteredColaboradores.length)

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">Colaboradores</h1>
            <p className="text-gray-600">Gerencie os colaboradores do condomínio</p>
            <div className="text-sm text-gray-500 mt-2 space-y-1">
              <p>Colaboradores ativos: {colaboradores?.length || 0}</p>
              <p>Colaboradores arquivados: {colaboradoresArquivados?.length || 0}</p>
            </div>
          </div>
          <NovoColaboradorModal />
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar colaboradores..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={filterType} onValueChange={(value) => setFilterType(value as StaffType | "all")}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as funções</SelectItem>
                <SelectItem value="porteiro">Porteiro</SelectItem>
                <SelectItem value="zelador">Zelador</SelectItem>
                <SelectItem value="jardineiro_piscineiro">Jardineiro/Piscineiro</SelectItem>
                <SelectItem value="auxiliar_servicos_gerais">Auxiliar de Serviços Gerais</SelectItem>
                <SelectItem value="gerente">Gerente</SelectItem>
                <SelectItem value="operador">Operador</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Ativos ({colaboradores?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              Arquivados ({colaboradoresArquivados?.length || 0})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active" className="mt-6">
            {filteredColaboradores.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {searchTerm || filterType !== "all" ? "Nenhum colaborador ativo encontrado para os filtros aplicados" : "Nenhum colaborador ativo cadastrado"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredColaboradores.map((colaborador) => (
                  <Card key={colaborador.id} className="ita-card">
                     <CardHeader className="pb-3">
                       <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <Avatar className="shrink-0">
                             {colaborador.photo_url ? (
                               <img 
                                 src={resolvePhotoUrl(colaborador.photo_url)} 
                                 alt={colaborador.full_name}
                                 className="object-cover w-full h-full rounded-full"
                               />
                             ) : (
                               <AvatarFallback className="bg-ita-blue text-white">
                                 {getInitials(colaborador.full_name)}
                               </AvatarFallback>
                             )}
                           </Avatar>
                           <div className="min-w-0">
                             <CardTitle className="text-lg font-semibold truncate">
                               {colaborador.full_name}
                             </CardTitle>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge className={getStaffTypeBadgeColor(colaborador.staff_type)}>
                                {getStaffTypeLabel(colaborador.staff_type)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => setEditModal({ open: true, colaborador })}
                          >
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Archive className="h-4 w-4 text-orange-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar arquivamento</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja arquivar {colaborador.full_name}? O colaborador ficará inativo mas poderá ser restaurado posteriormente.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleArchiveStaff(colaborador.id, colaborador.full_name)}
                                  className="bg-orange-500 hover:bg-orange-600"
                                >
                                  Arquivar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                     <CardContent className="space-y-2">
                       {colaborador.cpf && (
                         <div className="flex items-center space-x-2 text-sm text-gray-600">
                           <span className="font-medium">CPF:</span>
                           <span>{colaborador.cpf}</span>
                         </div>
                       )}
                       {colaborador.workload_hours && (
                         <div className="flex items-center space-x-2 text-sm text-gray-600">
                           <span className="font-medium">Jornada:</span>
                           <span>{colaborador.workload_hours}h</span>
                         </div>
                       )}
                       {colaborador.phone && (
                         <div className="flex items-center space-x-2 text-sm text-gray-600">
                           <Phone className="h-4 w-4 text-ita-blue" />
                           <span>{colaborador.phone}</span>
                         </div>
                       )}
                       {colaborador.email && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600 min-w-0">
                            <Mail className="h-4 w-4 text-ita-blue shrink-0" />
                            <span className="truncate">{colaborador.email}</span>
                          </div>
                       )}
                     </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="archived" className="mt-6">
            {isLoadingArchived ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="text-gray-600 mt-2">Carregando colaboradores arquivados...</p>
              </div>
            ) : filteredArquivados.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {searchTerm || filterType !== "all" ? "Nenhum colaborador arquivado encontrado para os filtros aplicados" : "Nenhum colaborador arquivado"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArquivados.map((colaborador) => (
                  <Card key={colaborador.id} className="ita-card border-orange-200 bg-orange-50">
                     <CardHeader className="pb-3">
                       <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <Avatar className="opacity-75 shrink-0">
                             {colaborador.photo_url ? (
                               <img 
                                 src={resolvePhotoUrl(colaborador.photo_url)} 
                                 alt={colaborador.full_name}
                                 className="object-cover w-full h-full rounded-full"
                               />
                             ) : (
                               <AvatarFallback className="bg-orange-400 text-white">
                                 {getInitials(colaborador.full_name)}
                               </AvatarFallback>
                             )}
                           </Avatar>
                           <div className="min-w-0">
                             <CardTitle className="text-lg font-semibold text-orange-800 truncate">
                               {colaborador.full_name}
                             </CardTitle>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge className="bg-orange-200 text-orange-800 border-orange-300">
                                {getStaffTypeLabel(colaborador.staff_type)}
                              </Badge>
                              <Badge variant="secondary" className="bg-orange-300 text-orange-900">
                                Arquivado
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <RotateCcw className="h-4 w-4 text-green-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Restaurar colaborador</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja restaurar {colaborador.full_name}? O colaborador voltará a ficar ativo no sistema.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRestoreStaff(colaborador.id, colaborador.full_name)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Restaurar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>⚠️ Excluir permanentemente</AlertDialogTitle>
                                <AlertDialogDescription className="space-y-2">
                                  <p>Tem certeza que deseja excluir permanentemente {colaborador.full_name}?</p>
                                  <p className="text-red-600 font-medium">
                                    ⚠️ ATENÇÃO: Esta ação NÃO pode ser desfeita e removerá:
                                  </p>
                                  <ul className="text-sm space-y-1 ml-4">
                                    <li>• O colaborador do sistema</li>
                                    <li>• Todos os registros de ponto</li>
                                    <li>• Dados de banco de horas</li>
                                    <li>• Perfil e acesso ao sistema</li>
                                  </ul>
                                  <p className="text-sm text-gray-600">
                                    Após a exclusão, o CPF poderá ser reutilizado para um novo colaborador.
                                  </p>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleHardDeleteStaff(colaborador.id, colaborador.full_name)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir Permanentemente
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                     <CardContent className="space-y-2">
                       {colaborador.cpf && (
                         <div className="flex items-center space-x-2 text-sm text-orange-700">
                           <span className="font-medium">CPF:</span>
                           <span>{colaborador.cpf}</span>
                         </div>
                       )}
                       {colaborador.workload_hours && (
                         <div className="flex items-center space-x-2 text-sm text-orange-700">
                           <span className="font-medium">Jornada:</span>
                           <span>{colaborador.workload_hours}h</span>
                         </div>
                       )}
                       {colaborador.phone && (
                         <div className="flex items-center space-x-2 text-sm text-orange-700">
                           <Phone className="h-4 w-4 text-orange-600" />
                           <span>{colaborador.phone}</span>
                         </div>
                       )}
                       {colaborador.email && (
                          <div className="flex items-center space-x-2 text-sm text-orange-700 min-w-0">
                            <Mail className="h-4 w-4 text-orange-600 shrink-0" />
                            <span className="truncate">{colaborador.email}</span>
                          </div>
                       )}
                     </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {editModal.colaborador && (
          <EditarColaboradorModal
            open={editModal.open}
            onOpenChange={(open) => setEditModal({ open, colaborador: open ? editModal.colaborador : undefined })}
            colaborador={editModal.colaborador}
          />
        )}
      </div>
    </Layout>
  )
}

export default Colaboradores